import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
  Breadcrumbs,
  Fade,
  Snackbar,
  Avatar,
  Grid,
  Stack,
  Collapse,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Group as GroupIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Share as ShareIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Business as BusinessIcon,
  BeachAccess as BeachIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  History as HistoryIcon,
  School as TrainingIcon,
  Handshake as MeetingIcon,
  Twitter as TwitterIcon,
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  WhatsApp as WhatsAppIcon,
  ContentCopy as CopyIcon,
  Email as EmailIcon,
  Language as LanguageIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Menu as MenuIcon,
  PictureAsPdf as PdfIcon,
  MenuBook as MenuBookIcon,
  SupportAgent as SupportAgentIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import { format, formatDistanceToNow, isBefore, isWithinInterval, differenceInDays } from 'date-fns';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { convertBudgetSummary, convertCostsForBudgetSummary } from '../utils/currencyUtils';
import { useUserPreferences } from '../hooks/useUserPreferences';
import TripGroupChat from '../components/TripGroupChat';
import TripAccessDenied from '../components/TripAccessDenied';
import TripMembers from '../components/TripMembers';
import TripMemberMap from '../components/TripMemberMap';
import TripItinerary from '../components/TripItinerary';
import TripExpenses from '../components/TripExpenses';
import TripChecklist from '../components/TripChecklist';
import TripGroupInvoice from '../components/groupBilling/TripGroupInvoice';
import TripMemoriesPanel from '../components/TripMemoriesPanel';
import TripMapExplorerNew from '../components/TripMapExplorerNew';
import WeatherWidget from '../components/WeatherWidget';
import { useAuth } from '../contexts/AuthContext';
import { isPartnerClientBrand, useSyncClientBranding } from '../contexts/ClientBrandingContext';
import PDFExportButton from '../components/PDFExportButton';
import CalendarExportButton from '../components/CalendarExportButton';
import PaywallBypassToggle from '../components/PaywallBypassToggle';
import TripModal from '../components/TripModal';
import HandOverTripToAgentDialog from '../components/HandOverTripToAgentDialog';
import { getTripPrimaryLocation, getTripAllLocations, getDisplayTripTitle, getTripCreatorLabel, filterTripTabs, canShowTripAiGenerate, canShowTripSuggestWithAi, canShowTripBookedBy, isManagedByPartner, canEditTripDetails, isGroupTrip, getTripTimelineStatus } from '../utils/tripUtils';
import { headerBgFromClientBranding } from '../utils/partnerWorkspaceBrand';
import { openTripAgentThread } from '../utils/openTripAgentThread';
import PartnerTripAIDrawer from '../components/PartnerTripAIDrawer';
import CreateOpsRequestDialog from '../components/CreateOpsRequestDialog';
import ShareReadOnlyLinkDialog from '../components/ShareReadOnlyLinkDialog';
import { openExternalURL } from '../utils/platformDetection';
import { recordTripEngagement } from '../utils/tripEngagement';

import { 
  THEME_GRADIENTS, 
  THEME_SHADOWS, 
  THEME_RADIUS,
  THEME_COLORS,
  COMMON_STYLES
} from '../constants/theme';
import { DOCUMENT, documentPageTabsSx, documentChatCountSx, documentTabIndicatorSx } from '../constants/documentTheme';
import BackgroundChatListener from '../components/BackgroundChatListener';
import PublishGuideDialog from '../components/PublishGuideDialog';
import useUnreadMessages from '../hooks/useUnreadMessages';
import PaymentWall from '../components/PaymentWall';
import TripFlightAlerts from '../components/TripFlightAlerts';
import TripDocuments from '../components/TripDocuments';
import ChangeHistoryPanel from '../components/ChangeHistoryPanel';
import TripPassBadge from '../components/TripPassBadge';
import PartnerLockup from '../components/PartnerLockup';
import PartnerLogoMark from '../components/PartnerLogoMark';
import TripBookedBy from '../components/TripBookedBy';
import { isUsableImageUrl } from '../utils/itineraryItemImage';
import usePremiumAccess from '../hooks/usePremiumAccess';
import OfflineItineraryBar from '../components/OfflineItineraryBar';
import EmailForwardingCard from '../components/EmailForwardingCard';
import useOfflineTrip from '../hooks/useOfflineTrip';
import useNetworkStatus from '../hooks/useNetworkStatus';

// Professional trip type configurations
const tripTypeConfig = {
  'business': { 
    icon: <BusinessIcon />, 
    color: THEME_COLORS.primary.main, 
    gradient: THEME_GRADIENTS.primary,
    label: 'Business Travel'
  },
  'conference': { 
    icon: <GroupIcon />, 
    color: THEME_COLORS.accent.main, 
    gradient: THEME_GRADIENTS.accent,
    label: 'Conference'
  },
  'training': { 
    icon: <TrainingIcon />, 
    color: THEME_COLORS.secondary.main, 
    gradient: THEME_GRADIENTS.secondary,
    label: 'Training'
  },
  'meeting': { 
    icon: <MeetingIcon />, 
    color: THEME_COLORS.categories.event.main, 
    gradient: THEME_GRADIENTS.primary,
    label: 'Meeting'
  },
  'vacation': { 
    icon: <BeachIcon />, 
    color: THEME_COLORS.primary.main, 
    gradient: THEME_GRADIENTS.primary,
    label: 'Vacation'
  },
  'default': { 
    icon: <BusinessIcon />, 
    color: THEME_COLORS.primary.main, 
    gradient: THEME_GRADIENTS.primary,
    label: 'Business Travel'
  }
};

/**
 * The tabs on a trip, in display order.
 *
 * Identified by key rather than position. The indices used to be written out at
 * every call site, which meant adding a tab required renumbering the panels, the
 * premium list and the URL mapping in step — and the numbering was already wrong
 * for partners, whose hidden tabs shift everything after them down.
 *
 * Travelling tabs are free for travellers. `partner` marks a tab a partner
 * sees on a client trip.
 *
 * Flight status chips live on itinerary stops. The separate Flights panel is
 * live gate/delay watches (`TripFlightAlerts`) and is not in this nav; ?tab=flights
 * still opens it.
 */
const TRIP_TABS = [
  { key: 'itinerary', labelKey: 'tripTabs.itinerary', premium: false, partner: true },
  { key: 'memories', labelKey: 'tripTabs.memories', premium: false, partner: true },
  { key: 'explore', labelKey: 'tripTabs.explore', premium: false, partner: false },
  { key: 'members', labelKey: 'tripTabs.members', premium: false, partner: true },
  { key: 'chat', labelKey: 'tripTabs.chat', premium: false, partner: false },
  { key: 'map', labelKey: 'tripTabs.map', premium: false, partner: false },
  { key: 'checklist', labelKey: 'tripTabs.checklist', premium: false, partner: true },
  { key: 'expenses', labelKey: 'tripTabs.expenses', premium: false, partner: false },
  { key: 'documents', labelKey: 'tripTabs.documents', premium: false, partner: false },
  { key: 'billing', labelKey: 'tripTabs.billing', premium: false, partner: false, managedOnly: true },
  { key: 'history', labelKey: 'changeHistory.historyTab', premium: false, partner: true, ownerOnly: true }
];

const tripHeaderOutlineBtnSx = {
  color: DOCUMENT.ivory,
  border: '1px solid rgba(246,239,228,0.35)',
  borderRadius: 0,
  textTransform: 'none',
  px: 1.5,
  py: 0.5,
  minHeight: 40,
  flex: { xs: '1 1 auto', sm: '0 0 auto' },
  minWidth: 0,
  maxWidth: '100%',
  '&:hover': { bgcolor: 'rgba(246,239,228,0.08)', borderColor: DOCUMENT.ivory },
};

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Fade in timeout={300}>
          <Box sx={{ py: { xs: 2, md: 3 } }}>
            {children}
          </Box>
        </Fade>
      )}
    </div>
  );
}

const TripDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user } = useAuth();
  const { t } = useTranslation();

  // Check if user is a partner
  const isPartner = !!localStorage.getItem('partnerToken');
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [headerExpanded, setHeaderExpanded] = useState(false);

  // Helper function to parse date without timezone conversion
  const parseDateWithoutTimezone = (dateStr) => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split('-');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  };
  const { refreshAccess } = usePremiumAccess(id);
  const [trip, setTrip] = useState(null);
  const tripBranding = trip?.branding;
  const partnerClientBrand = isPartnerClientBrand(tripBranding);
  const shareBrandName = partnerClientBrand ? tripBranding.name : 'Déjà Vu Nomad';
  const brandColor = partnerClientBrand && !tripBranding.applyTheme
    ? tripBranding.color
    : DOCUMENT.oxblood;
  useSyncClientBranding(tripBranding);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [itineraryItems, setItineraryItems] = useState([]);
  const [memberItineraryItems, setMemberItineraryItems] = useState([]);

  // Offline access: a Trip Pass feature. `viewingSnapshot` means what is on
  // screen came from local storage rather than the server, which the traveller
  // needs to be told so they don't trust a stale gate number.
  const { isOnline } = useNetworkStatus();
  const offlineTrip = useOfflineTrip(id, user?.id);
  const [viewingSnapshot, setViewingSnapshot] = useState(false);
  // Read inside fetchTripDetails without making the fetch depend on the
  // snapshot, which would refetch the trip every time someone saves it.
  const snapshotRef = useRef(null);
  snapshotRef.current = offlineTrip.snapshot;

  // User preferences for currency conversion
  const { preferences: userPreferences } = useUserPreferences();

  // Debug state changes (can be removed in production)
  // useEffect(() => {
  //   console.log('TripDetails: Loading state changed to:', loading);
  // }, [loading]);

  // useEffect(() => {
  //   console.log('TripDetails: Trip state changed to:', trip);
  // }, [trip]);

  // useEffect(() => {
  //   console.log('TripDetails: Error state changed to:', error);
  // }, [error]);
  
  // Add abort controller for request cancellation
  const abortControllerRef = useRef(null);
  const fetchingRef = useRef(false); // Track if we're currently fetching
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveError, setArchiveError] = useState('');
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [tabValue, setTabValue] = useState('itinerary');
  const [itineraryInitialTab, setItineraryInitialTab] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [publishGuideOpen, setPublishGuideOpen] = useState(false);
  const [handoverDialogOpen, setHandoverDialogOpen] = useState(false);
  const [headerMenuAnchor, setHeaderMenuAnchor] = useState(null);
  const tripMembersRef = useRef(); // Ref to access TripMembers functions
  const tripItineraryRef = useRef(); // Ref to access TripItinerary functions
  const pdfExportRef = useRef(); // Ref to access PDF export button
  const calendarExportRef = useRef(null);
  const isMountedRef = useRef(true); // Track if component is mounted
  const engagementRecordedRef = useRef(false);

  // Handle payment wall close and refresh access
  const handlePaymentWallClose = () => {
    setPaymentWallOpen(false);
    setBlockedFeature('');
    // Refresh premium access status
    refreshAccess();
  };


  const { unreadCounts, markAsRead } = useUnreadMessages([parseInt(id, 10)]);
  const stickyUnreadCount = unreadCounts[parseInt(id, 10)] || 0;
  
  // Helper function for backward compatibility - wrapped in useCallback to prevent dependency changes
  const markTripAsRead = useCallback((tripId) => {
    const lastRead = localStorage.getItem(`lastReadMessage_${tripId}`);
    if (lastRead) {
      markAsRead(tripId, parseInt(lastRead, 10));
    }
  }, [markAsRead]);

  // Clear unread count when switching to chat
  useEffect(() => {
    if (tabValue === 'chat') {
      const lastRead = localStorage.getItem(`lastReadMessage_${id}`);
      if (lastRead) {
        markTripAsRead(parseInt(id, 10));
      }
    }
  }, [tabValue, id, markTripAsRead]);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [paymentWallOpen, setPaymentWallOpen] = useState(false);
  const [blockedFeature, setBlockedFeature] = useState('');

  // 1. Add state for email share dialog
  const [emailShareDialogOpen, setEmailShareDialogOpen] = useState(false);
  const [emailShareLoading, setEmailShareLoading] = useState(false);
  const [emailShareError, setEmailShareError] = useState('');
  const [emailShareSuccess, setEmailShareSuccess] = useState('');
  const [emailShareRecipient, setEmailShareRecipient] = useState('');
  const [emailShareMessage, setEmailShareMessage] = useState('');
  const [readOnlyShareOpen, setReadOnlyShareOpen] = useState(false);

  // Leave trip state
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [messagingAgent, setMessagingAgent] = useState(false);
  const [requestChangeOpen, setRequestChangeOpen] = useState(false);
  const [suggestAiOpen, setSuggestAiOpen] = useState(false);

  // Partner details modal state
  const [partnerDetailsOpen, setPartnerDetailsOpen] = useState(false);

  const isCreator = useMemo(() => {
    const result = trip?.created_by === user?.id || trip?.is_creator === true;
    console.log('[DEBUG] isCreator check:', {
      tripCreatedBy: trip?.created_by,
      userId: user?.id,
      tripIsCreator: trip?.is_creator,
      result
    });
    return result;
  }, [trip?.created_by, trip?.is_creator, user?.id]);

  const canArchivePastTrip = Boolean(trip)
    && !isPartner
    && getTripTimelineStatus(trip) === 'past'
    && (isCreator || trip?.user_role === 'member' || trip?.user_role === 'creator' || trip?.user_role === 'admin');

  const handleArchiveTrip = useCallback(async () => {
    if (!canArchivePastTrip) return;
    try {
      setArchiveLoading(true);
      setArchiveError('');
      await api.patch(`/trips/${id}/archive`, { archived: true });
      setArchiveDialogOpen(false);
      navigate('/trips');
    } catch (err) {
      setArchiveError(
        err.response?.data?.error || t('trip.archiveDialog.failed', { defaultValue: 'Failed to archive trip.' })
      );
    } finally {
      setArchiveLoading(false);
    }
  }, [canArchivePastTrip, id, navigate, t]);

  const canChangeAdvisor = Boolean((isCreator || trip?.admin_view) && !isPartner);

  // Calculate dynamic member count based on user role and visibility
  const visibleMemberCount = useMemo(() => {
    if (!trip || !trip.all_participants) {
      return trip?.member_count || 0;
    }

    const participants = trip.all_participants;
    
    // Debug logging to understand the data structure
    console.log('🔍 All participants:', participants);
    console.log('🔍 Participant statuses:', participants.map(p => ({ email: p.email, status: p.status, status_text: p.status_text })));
    
    // Partners and creators see all participants (members + pending invitations)
    if (isCreator || isPartner) {
      // Filter out any duplicates by email and only count valid entries
      const uniqueParticipants = participants.filter((p, index, arr) => {
        // Skip entries without email
        if (!p.email) return false;
        
        // Remove duplicates by email (keep first occurrence)
        const firstIndex = arr.findIndex(item => item.email === p.email);
        return firstIndex === index;
      });
      
      console.log('🔍 Unique participants for creator/partner:', uniqueParticipants.length);
      console.log('🔍 Unique emails:', uniqueParticipants.map(p => p.email));
      return uniqueParticipants.length;
    } else {
      // Regular members only see actual members (not pending invitations)
      const actualMembers = participants.filter(p => 
        (p.status === 'creator' || p.status === 'member') && p.email
      );
      console.log('🔍 Actual members for non-creator:', actualMembers.length);
      console.log('🔍 Member emails:', actualMembers.map(p => p.email));
      return actualMembers.length;
    }
  }, [trip, isCreator, isPartner]);

  const tripTypeInfo = useMemo(() => {
    return tripTypeConfig[trip?.trip_type?.toLowerCase()] || tripTypeConfig.default;
  }, [trip?.trip_type]);

  // Get trip status
  const getTripStatus = useCallback(() => {
    if (!trip) return { status: 'unknown', label: 'Unknown', color: 'default' };
    
    const now = new Date();
    const startDate = parseDateWithoutTimezone(trip.start_date);
    const endDate = parseDateWithoutTimezone(trip.end_date);
    
    if (isBefore(now, startDate)) {
      const daysUntil = formatDistanceToNow(startDate, { addSuffix: true });
      return { 
        status: 'upcoming', 
        label: t('trip.statusUpcoming'), 
        daysText: daysUntil, 
        color: 'primary',
        icon: <ScheduleIcon />
      };
    } else if (isWithinInterval(now, { start: startDate, end: endDate })) {
      const daysLeft = formatDistanceToNow(endDate, { addSuffix: true });
      return { 
        status: 'current', 
        label: t('trip.statusInProgress'), 
        daysText: `${t('trip.statusEnds')} ${daysLeft}`, 
        color: 'success',
        icon: <CheckCircleIcon />
      };
    } else {
      const daysAgo = formatDistanceToNow(endDate, { addSuffix: true });
      return { 
        status: 'past', 
        label: t('trip.statusCompleted'), 
        daysText: `${t('trip.statusEnded')} ${daysAgo}`, 
        color: 'default',
        icon: <HistoryIcon />
      };
    }
  }, [trip, t]);

  const tripStatus = getTripStatus();
  const tripMapsUrl = useMemo(() => {
    const query = getTripPrimaryLocation(trip);
    if (!query) return null;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
  }, [trip]);

  // 2. Utility to generate itinerary summary (stub)
  const generateItinerarySummary = useCallback((itinerary, trip) => {
    // Group itinerary items by date
    // Helper function to parse datetime without timezone conversion
    const parseDateTimeWithoutTimezone = (dateTimeStr) => {
      if (!dateTimeStr) return null;
      const [datePart, timePart] = dateTimeStr.split(' ');
      const [year, month, day] = datePart.split('-');
      const [hours, minutes, seconds] = timePart ? timePart.split(':') : ['00', '00', '00'];
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hours), parseInt(minutes), parseInt(seconds));
    };

    const itemsByDate = {};
    itinerary.forEach(item => {
      const date = parseDateTimeWithoutTimezone(item.start_datetime).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      });
      if (!itemsByDate[date]) {
        itemsByDate[date] = [];
      }
      itemsByDate[date].push(item);
    });

    // Text summary
    let text = `Trip: ${trip?.title}\nLocation: ${getTripPrimaryLocation(trip)}\nDates: ${trip?.start_date} - ${trip?.end_date}\n\nDaily Itinerary:\n\n`;
    
    // HTML summary (basic version - backend will handle full formatting)
    let html = `<h2>${trip?.title}</h2><p><strong>Location:</strong> ${getTripPrimaryLocation(trip)}</p><p><strong>Dates:</strong> ${trip?.start_date} - ${trip?.end_date}</p><h3>Daily Itinerary</h3>`;

    // Add each day's events
    Object.entries(itemsByDate).forEach(([date, items]) => {
      text += `${date}:\n`;
      html += `<h4>${date}</h4><ul>`;
      
      items.forEach(item => {
        const startTime = parseDateTimeWithoutTimezone(item.start_datetime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
        const endTime = item.end_datetime ? parseDateTimeWithoutTimezone(item.end_datetime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'All day';
        const timeStr = `${startTime}${endTime !== 'All day' ? ` – ${endTime}` : ''}`;
        
        text += `- ${timeStr}: ${item.name}`;
        if (item.location) text += `\n  Where: ${item.location}`;
        if (item.description) text += `\n  Details: ${item.description}`;
        text += '\n';

        html += `<li><strong>${timeStr}:</strong> ${item.name}`;
        if (item.location) html += ` <em>(${item.location})</em>`;
        if (item.description) html += ` - ${item.description}`;
        html += '</li>';
      });

      text += '\n';
      html += '</ul>';
    });

    // Add signup information
    const frontendUrl = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
    const shareBrand = trip?.branding?.level && trip.branding.level !== 'house' && trip.branding.name
      ? trip.branding.name
      : 'Déjà Vu Nomad';
    text += `\n🌍 Want to Join This Trip?\n\nCreate your free ${shareBrand} account to collaborate on trip planning!\n\nSign Up: ${frontendUrl}/register\nLogin: ${frontendUrl}/login`;
    html += `<p><strong>Want to Join This Trip?</strong><br/>Create your free account to collaborate on trip planning!<br/><a href="${frontendUrl}/register">Sign Up Free</a> | <a href="${frontendUrl}/login">Login</a></p>`;

    return { text, html };
  }, []);

  // 3. Add handler for sending email
  const handleSendItineraryEmail = async () => {
    setEmailShareLoading(true);
    setEmailShareError('');
    setEmailShareSuccess('');
    try {
      // Fetch itinerary data (reuse TripItinerary logic or API)
      const res = await api.get(`/trips/${id}/itinerary`);
      const itinerary = res.data;
      const summary = generateItinerarySummary(itinerary, trip);
      await api.post(`/trips/${id}/share-itinerary`, {
        email: emailShareRecipient,
        message: emailShareMessage,
        summaryText: summary.text,
        summaryHtml: summary.html
      });
      setEmailShareSuccess('Itinerary summary sent successfully!');
      setEmailShareDialogOpen(false);
    } catch (err) {
      setEmailShareError(err.response?.data?.error || 'Failed to send itinerary email');
    } finally {
      setEmailShareLoading(false);
    }
  };

  const fetchItineraryItems = useCallback(async () => {
    try {
      // Fetch main itinerary items
      const itineraryResponse = await api.get(`/trips/${id}/itinerary`);
      setItineraryItems(itineraryResponse.data || []);
      
      // Fetch member itinerary items
      const memberItineraryResponse = await api.get(`/member-itinerary/trip/${id}`);
      setMemberItineraryItems(memberItineraryResponse.data?.items || memberItineraryResponse.data || []);
    } catch (err) {
      console.error('Error fetching itinerary items:', err);
      // Don't set error state for itinerary items, just log it
    }
  }, [id]);

  const fetchTripDetails = useCallback(async () => {
    try {
      if (!isMountedRef.current) {
        return;
      }

      if (fetchingRef.current) {

        return;
      }

      // Set fetching flag
      fetchingRef.current = true;

      // Only cancel existing request if there actually is one
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();
      
      setError(null);
      setAccessDenied(false);
      
      const response = await api.get(`/trips/${id}`, {
        signal: abortControllerRef.current.signal,
        timeout: 10000 // 10 second timeout
      });
      
      // Check if component is still mounted before setting state
      if (isMountedRef.current && response?.data) {
        setTrip(response.data);
        setViewingSnapshot(false);

        if (
          (response.data.partner_id || response.data.managed_by_partner)
          && !engagementRecordedRef.current
        ) {
          engagementRecordedRef.current = true;
          recordTripEngagement(api, id, {
            eventType: 'proposal_view',
            email: user?.email,
          });
        }
        
        // Fetch itinerary items for budget summary
        await fetchItineraryItems();
      }
    } catch (err) {
      // Only set error if component is mounted and it's not a cancellation
      if (isMountedRef.current && err.name !== 'AbortError') {
        // A saved copy is the whole point of the offline feature: on a travel
        // day with no signal, show the itinerary instead of an error.
        const snapshot = snapshotRef.current;
        if (snapshot?.trip) {
          setTrip(snapshot.trip);
          setItineraryItems(snapshot.itinerary || []);
          setMemberItineraryItems(snapshot.memberItinerary || []);
          setViewingSnapshot(true);
          return;
        }

        const status = err.response?.status;
        setAccessDenied(status === 403 || status === 404);
        const errorMessage = err.code === 'ECONNABORTED' || err.message.includes('timeout') 
          ? 'Request timed out. Please try again.' 
          : 'Failed to fetch trip details';
        setError(errorMessage);
      }
    } finally {
      // Reset fetching flag
      fetchingRef.current = false;
      
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [id, fetchItineraryItems, user?.email]);

  useEffect(() => {
    engagementRecordedRef.current = false;
  }, [id]);

  // The snapshot read races the trip request on load. If the request lost and
  // the snapshot only arrives afterwards, swap the error for the saved copy.
  useEffect(() => {
    const snapshot = offlineTrip.snapshot;
    if (!error || trip || !snapshot?.trip) return;

    setTrip(snapshot.trip);
    setItineraryItems(snapshot.itinerary || []);
    setMemberItineraryItems(snapshot.memberItinerary || []);
    setViewingSnapshot(true);
    setError(null);
  }, [error, trip, offlineTrip.snapshot]);

  // Callback to refresh trip data when itinerary changes
  const handleItineraryChange = useCallback(() => {
    // Refresh trip data to update budget calculation
    fetchTripDetails();
  }, [fetchTripDetails]);

  useEffect(() => {
    // Small delay to handle React Strict Mode double-mounting
    const timeoutId = setTimeout(() => {
      if (isMountedRef.current) {
        fetchTripDetails();
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [fetchTripDetails]);

  // Refetch trip data when the route changes (e.g., returning from edit page)
  useEffect(() => {
    // If we're on the trip details page and not loading, refetch
    if (location.pathname === `/trips/${id}` && !loading && isMountedRef.current) {
      console.log('Route changed to trip details - refetching trip data');
      fetchTripDetails();
    }
  }, [location.pathname, id, loading, fetchTripDetails]);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    // Set mounted to true when component mounts
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      fetchingRef.current = false;
      // Cancel any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const canViewHistory = isPartner ? trip?.user_role === 'partner' : isCreator;

  const visibleTabs = useMemo(
    () => filterTripTabs(TRIP_TABS, {
      isPartner,
      managedByPartner: isManagedByPartner(trip),
      canViewHistory,
    }),
    [isPartner, trip, canViewHistory]
  );

  const openTripAgentMessages = useCallback(async () => {
    if (!trip?.id || messagingAgent) return;
    try {
      setMessagingAgent(true);
      await openTripAgentThread({ trip, api, navigate });
    } catch (err) {
      console.error('Error opening trip message thread:', err);
      setSnackbarMessage(err.response?.data?.error || t('myPackageBooking.messageFailed'));
      setSnackbarOpen(true);
    } finally {
      setMessagingAgent(false);
    }
  }, [trip, messagingAgent, navigate, t]);

  // ?tab= now carries the tab key directly, so a new tab needs no mapping here.
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');

    // Suggestions and add-ons live inside the itinerary sub-tabs; old links still work.
    if (tabParam === 'suggestions') {
      setItineraryInitialTab('suggestions');
      setTabValue('itinerary');
      return;
    }
    if (tabParam === 'addons') {
      setItineraryInitialTab('addons');
      setTabValue('itinerary');
      return;
    }

    // A partner following a link to a tab they cannot see would otherwise land
    // on a page with no selected tab and no content.
    if (tabParam === 'flights') {
      setTabValue('flights');
      return;
    }
    if (visibleTabs.some((tab) => tab.key === tabParam)) {
      setTabValue(tabParam);
    }
  }, [location.search, visibleTabs]);

  const handleDelete = useCallback(async () => {
    if (!isCreator) return;

    try {
      setDeleteLoading(true);
      await api.delete(`/trips/${id}`);
      navigate('/trips');
    } catch (err) {
      setError('Failed to delete trip');
    } finally {
      setDeleteLoading(false);
    }
  }, [id, isCreator, navigate]);

  const handleLeaveTrip = useCallback(async () => {
    try {
      setLeaveLoading(true);
      await api.post(`/trips/${id}/leave`);
      navigate('/trips');
    } catch (err) {
      console.error('Error leaving trip:', err);
      // You could add error state here if needed
    } finally {
      setLeaveLoading(false);
      setLeaveDialogOpen(false);
    }
  }, [id, navigate]);

  const handleInvite = useCallback(async (e) => {
    e.preventDefault();
    if (!inviteEmail || !isMountedRef.current) return;

    try {
      setInviteError('');

      // Inviting is free at any group size, so there is nothing to check first.

      // Add optimistic invitation immediately
      if (tripMembersRef.current?.addOptimisticInvitation) {
        tripMembersRef.current.addOptimisticInvitation(inviteEmail);
      }
      
      await api.post(`/trips/${id}/invite`, { email: inviteEmail });
      
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setInviteEmail('');
        setShowInviteDialog(false);
        fetchTripDetails();
      }
    } catch (err) {
      console.error('Invitation error:', err);
      
      // Only handle error if component is mounted and not cancelled
      if (isMountedRef.current && err.name !== 'AbortError') {
        // Check if this is a member limit error from the backend (skip for partners)
        if (!isPartner && err.response?.status === 403 && err.response?.data?.error?.includes('member limit')) {
          setShowInviteDialog(false);
          setInviteError(err.response?.data?.error || 'Failed to send invitation');
          return;
        }
        
        setInviteError(err.response?.data?.error || 'Failed to send invitation');
        
        // Remove optimistic invitation on error
        if (tripMembersRef.current?.removeOptimisticInvitation) {
          tripMembersRef.current.removeOptimisticInvitation(inviteEmail);
        }
      }
    }
  }, [id, inviteEmail, fetchTripDetails, isPartner]);

  const handleUnreadCountChange = useCallback(() => {
    // If we're on the chat tab, immediately clear any unread count
    if (tabValue === 'chat') {
      const lastRead = localStorage.getItem(`lastReadMessage_${id}`);
      if (lastRead) {
        markTripAsRead(parseInt(id, 10));
      }
    }
    // No need for local count - the centralized sticky count will handle persistence
  }, [tabValue, id, markTripAsRead]);

  // Custom tab change handler that clears notifications when switching to chat
  const handleTabChange = useCallback((event, newValue) => {
    setTabValue(newValue);

    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tab', newValue);

    // Update URL without triggering navigation
    const newSearch = searchParams.toString();
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    navigate(newUrl, { replace: true });

    // If switching to chat tab and there are unread messages, clear them
    if (newValue === 'chat' && stickyUnreadCount > 0) {
      const lastRead = localStorage.getItem(`lastReadMessage_${id}`);
      if (lastRead) {
        markTripAsRead(parseInt(id, 10));
      }
    }
  }, [id, markTripAsRead, stickyUnreadCount, location.search, location.pathname, navigate]);

  // Debounced navigation handler to prevent multiple rapid clicks
  const handleNavigateToTrips = useCallback(() => {
    // Don't navigate if we're in the middle of loading operations
    if (!loading && !deleteLoading) {
      navigate('/trips');
    }
  }, [navigate, loading, deleteLoading]);

  // Social media sharing functionality
  const handleShare = useCallback(() => {
    const tripUrl = `${window.location.origin}/trips/${id}`;
    const tripTypeLabel = tripTypeInfo.label.toLowerCase();
    
    // Create creative message based on trip type and status
    let baseMessage = "";
    const location = getTripPrimaryLocation(trip);
    if (trip?.trip_type === 'vacation') {
      baseMessage = `🌴 Join me on an amazing ${tripTypeLabel} to ${location}! `;
    } else if (trip?.trip_type === 'business') {
      baseMessage = `💼 Coordinating ${tripTypeLabel} to ${location}. `;
    } else if (trip?.trip_type === 'conference') {
      baseMessage = `🎯 Attending ${tripTypeLabel} in ${location}! `;
    } else if (trip?.trip_type === 'adventure') {
      baseMessage = `🏔️ Adventure awaits! Join our ${tripTypeLabel} to ${location}! `;
    } else {
      baseMessage = `✈️ Planning an exciting trip to ${location}! `;
    }
    
    // Add trip details
    const startDate = format(parseDateWithoutTimezone(trip.start_date), 'MMM d, yyyy');
    const endDate = format(parseDateWithoutTimezone(trip.end_date), 'MMM d, yyyy');
    const dateText = startDate === endDate ? startDate : `${startDate} - ${endDate}`;
    
    let statusEmoji = "";
    if (tripStatus.status === 'upcoming') {
      statusEmoji = "⏰ ";
    } else if (tripStatus.status === 'current') {
      statusEmoji = "🎉 ";
    } else {
      statusEmoji = "📸 ";
    }
    
    const shareText = `${baseMessage}${statusEmoji}${dateText}\n\n` +
      `${trip?.description ? `"${trip.description}"\n\n` : ''}` +
      `Plan with us on ${shareBrandName}! 🌍\n${tripUrl}`;

    // Check if native sharing is available (mobile devices)
    if (navigator.share) {
      navigator.share({
        title: `${trip?.title} - ${shareBrandName}`,
        text: shareText,
        url: tripUrl
      }).catch((error) => {

        setShareDialogOpen(true);
      });
    } else {
      setShareDialogOpen(true);
    }
  }, [trip, tripStatus, tripTypeInfo, id, shareBrandName]);

  // Generate share text and URL for use in dialog
  const getShareData = useCallback(() => {
    const tripTypeLabel = tripTypeInfo.label.toLowerCase();
    
    let baseMessage = "";
    const location = getTripPrimaryLocation(trip);
    if (trip?.trip_type === 'vacation') {
      baseMessage = `🌴 Join me on an amazing ${tripTypeLabel} to ${location}! `;
    } else if (trip?.trip_type === 'business') {
      baseMessage = `💼 Coordinating ${tripTypeLabel} to ${location}. `;
    } else if (trip?.trip_type === 'conference') {
      baseMessage = `🎯 Attending ${tripTypeLabel} in ${location}! `;
    } else if (trip?.trip_type === 'adventure') {
      baseMessage = `🏔️ Adventure awaits! Join our ${tripTypeLabel} to ${location}! `;
    } else {
      baseMessage = `✈️ Planning an exciting trip to ${location}! `;
    }
    
    const startDate = format(parseDateWithoutTimezone(trip.start_date), 'MMM d, yyyy');
    const endDate = format(parseDateWithoutTimezone(trip.end_date), 'MMM d, yyyy');
    const dateText = startDate === endDate ? startDate : `${startDate} - ${endDate}`;
    
    let statusEmoji = "";
    if (tripStatus.status === 'upcoming') {
      statusEmoji = "⏰ ";
    } else if (tripStatus.status === 'current') {
      statusEmoji = "🎉 ";
    } else {
      statusEmoji = "📸 ";
    }
    
    const shareText = `${baseMessage}${statusEmoji}${dateText}\n\n` +
      `${trip?.description ? `"${trip.description}"\n\n` : ''}` +
      `Want to join? Sign up for a free account at ${shareBrandName} to collaborate on trip planning! 🌍`;
    
    return { shareText };
  }, [trip, tripStatus, tripTypeInfo, shareBrandName]);

  const handleCopyLink = useCallback(() => {
    const { shareText } = getShareData();
    navigator.clipboard.writeText(shareText).then(() => {
      setSnackbarMessage('Text copied to clipboard!');
      setSnackbarOpen(true);
      setShareDialogOpen(false);
    });
  }, [getShareData]);

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
        <CircularProgress size={60} />
      </Container>
    );
  }

  if (!trip) {
    if (accessDenied) return <TripAccessDenied tripId={id} />;
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert
          severity="error"
          sx={{
            borderRadius: THEME_RADIUS.small,
            ...COMMON_STYLES.professionalCard
          }}
          action={(
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              {t('trip.retryLoad')}
            </Button>
          )}
        >
          {error || t('trip.loadFailed')}
        </Alert>
      </Container>
    );
  }

  const displayTripTitle = getDisplayTripTitle(trip) || t('trip.details', { defaultValue: 'Trip Details' });
  const headerDateLine = trip?.start_date && trip?.end_date
    ? `${format(parseDateWithoutTimezone(trip.start_date), 'EEE, MMM d')} – ${format(parseDateWithoutTimezone(trip.end_date), 'EEE, MMM d, yyyy')}`
    : '';
  const headerCoverUrl = isUsableImageUrl(trip?.image_url) ? trip.image_url : null;

  const headerActionButtonSx = {
    color: DOCUMENT.ivory,
    width: { xs: 32, sm: 32 },
    height: { xs: 32, sm: 32 },
    bgcolor: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(201,169,97,0.35)',
    boxShadow: 'none',
    borderRadius: '50%',
    '&:hover': {
      bgcolor: 'rgba(255,255,255,0.14)',
      borderColor: DOCUMENT.accent,
      boxShadow: 'none',
    },
  };

  const headerActionIconSx = { fontSize: { xs: 18, sm: 19 } };

  const headerActionCellSx = (column, row) => ({
    gridColumn: column,
    gridRow: row,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  });

  const headerActionsGridSx = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 40px)',
    gridTemplateRows: 'repeat(2, 40px)',
    gap: { xs: 0.5, sm: 0.75 },
    flexShrink: 0,
    alignItems: 'center',
    justifyItems: 'center',
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: DOCUMENT.canvas, color: DOCUMENT.ink }}>
      {/* Compact Hero Header */}
      <Box
        sx={{
          bgcolor: brandColor,
          backgroundImage: headerCoverUrl ? `url(${headerCoverUrl})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: 'white',
          pt: { xs: 1, md: 1.5 },
          pb: headerExpanded ? { xs: 2.5, md: 3 } : { xs: 1.25, md: 1.5 },
          px: { xs: 2, md: 0 },
          position: 'relative',
          overflow: 'hidden',
          minHeight: headerCoverUrl
            ? (headerExpanded ? { xs: 200, md: 240 } : { xs: 140, md: 160 })
            : undefined,
          ...(headerCoverUrl ? {
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: [
                'linear-gradient(105deg, rgba(36,19,24,0.72) 0%, rgba(36,19,24,0.42) 48%, rgba(36,19,24,0.18) 100%)',
                'linear-gradient(180deg, rgba(36,19,24,0.18) 0%, rgba(36,19,24,0.36) 100%)',
              ].join(', '),
              pointerEvents: 'none',
            },
          } : {}),
        }}
      >
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              position: { xs: 'relative', md: 'absolute' },
              top: { md: 0 },
              left: { xs: 'auto', md: '50%' },
              transform: { xs: 'none', md: 'translateX(-50%)' },
              zIndex: { xs: 1, md: 3 },
              display: 'flex',
              justifyContent: 'center',
              width: { xs: '100%', md: 'max-content' },
              maxWidth: '100%',
              mb: { xs: 1.25, md: 0 },
              pointerEvents: 'auto',
            }}
            onClick={(event) => event.stopPropagation()}
          >
            {headerExpanded && (
              <WeatherWidget
                variant="header"
                headerSize={isMobile ? 'default' : 'large'}
                location={getTripPrimaryLocation(trip)}
                tripDates={{ start: trip?.start_date, end: trip?.end_date }}
              />
            )}
          </Box>
          <Box sx={{ position: 'relative', pt: headerExpanded ? { xs: 0, md: 7 } : 0 }}>
          {/* Breadcrumb + title (left column) | actions (top right) */}
          <Box 
            onClick={() => setHeaderExpanded(!headerExpanded)}
            sx={{ 
              position: 'relative',
              mb: headerExpanded ? { xs: 1.25, sm: 1.5 } : 0.5,
              cursor: 'pointer',
              borderRadius: 2,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) auto',
                gridTemplateRows: 'auto auto',
                gridTemplateAreas: `
                  "breadcrumb actions"
                  "title actions"
                `,
                columnGap: { xs: 1, sm: 1.5, md: 3 },
                rowGap: { xs: 0.35, sm: 0.75 },
                alignItems: 'start',
              }}
            >
              <Box sx={{ gridArea: 'breadcrumb', minWidth: 0 }}>
                <Breadcrumbs 
                  separator="›" 
                  sx={{ 
                    '& .MuiBreadcrumbs-separator': { color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.8rem' },
                    '& .MuiBreadcrumbs-li': { color: 'rgba(255, 255, 255, 0.9)' },
                    '& .MuiBreadcrumbs-ol': {
                      flexWrap: { xs: 'wrap', sm: 'nowrap' },
                      margin: 0,
                      padding: 0,
                    },
                  }}
                >
                  <Button
                    onClick={handleNavigateToTrips}
                    variant="text"
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.8)',
                      textTransform: 'none',
                      minWidth: 'auto',
                      p: 0,
                      fontSize: '0.8rem',
                      '&:hover': { color: 'white', textDecoration: 'underline', backgroundColor: 'transparent' }
                    }}
                  >
                    {t('trip.myTrips')}
                  </Button>
                  <Typography sx={{ 
                    color: 'white', 
                    fontWeight: 600, 
                    fontSize: '0.8rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: { xs: 'none', sm: 'block' },
                    maxWidth: { sm: 220, md: 320 },
                  }}>
                    {displayTripTitle}
                  </Typography>
                </Breadcrumbs>
              </Box>

              <Box
                sx={{
                  gridArea: 'actions',
                  gridRow: '1 / 3',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignSelf: 'start',
                  maxWidth: '100%',
                }}
                onClick={(event) => event.stopPropagation()}
              >
              <Box sx={headerActionsGridSx}>
                <Box sx={headerActionCellSx(1, 1)}>
                  <Tooltip title={isFavorite ? 'Remove favorite' : 'Add favorite'} arrow>
                    <IconButton
                      onClick={() => setIsFavorite(!isFavorite)}
                      size="small"
                      sx={headerActionButtonSx}
                    >
                      {isFavorite ? <FavoriteIcon sx={headerActionIconSx} /> : <FavoriteBorderIcon sx={headerActionIconSx} />}
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={headerActionCellSx(2, 1)}>
                  <Tooltip title={t('common.share', { defaultValue: 'Share' })} arrow>
                    <IconButton
                      onClick={handleShare}
                      size="small"
                      sx={headerActionButtonSx}
                    >
                      <ShareIcon sx={headerActionIconSx} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={headerActionCellSx(3, 1)}>
                  <Tooltip title={t('trip.menu')} arrow>
                    <IconButton
                      onClick={(event) => setHeaderMenuAnchor(event.currentTarget)}
                      size="small"
                      sx={headerActionButtonSx}
                      aria-label={t('trip.menu')}
                    >
                      <MenuIcon sx={headerActionIconSx} />
                    </IconButton>
                  </Tooltip>
                </Box>
                {(isCreator || isPartner) && (
                  <>
                    {canEditTripDetails(trip, { isCreator, isPartner }) && (
                      <Box sx={headerActionCellSx(1, 2)}>
                        <Tooltip title={t('trip.editTrip')} arrow>
                          <IconButton
                            size="small"
                            onClick={() => setEditModalOpen(true)}
                            sx={headerActionButtonSx}
                            data-testid="trip-edit-button"
                          >
                            <EditIcon sx={headerActionIconSx} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                    <Box sx={headerActionCellSx(2, 2)}>
                      <Tooltip title={t('trip.delete')} arrow>
                        <IconButton
                          size="small"
                          onClick={() => setDeleteDialogOpen(true)}
                          sx={headerActionButtonSx}
                        >
                          <DeleteIcon sx={headerActionIconSx} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </>
                )}
                {!isCreator && !isPartner && (
                  <Box sx={headerActionCellSx(1, 2)}>
                    <Tooltip title="Leave Trip" arrow>
                      <IconButton
                        size="small"
                        onClick={() => setLeaveDialogOpen(true)}
                        sx={headerActionButtonSx}
                      >
                        <DeleteIcon sx={headerActionIconSx} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
                <Box sx={headerActionCellSx(3, 2)}>
                  <IconButton
                    size="small"
                    aria-label={headerExpanded ? t('common.showLess') : t('trip.showDetails')}
                    data-testid="trip-header-toggle"
                    onClick={(event) => {
                      event.stopPropagation();
                      setHeaderExpanded(!headerExpanded);
                    }}
                    sx={headerActionButtonSx}
                  >
                    {headerExpanded ? <ExpandLessIcon sx={headerActionIconSx} /> : <ExpandMoreIcon sx={headerActionIconSx} />}
                  </IconButton>
                </Box>
              </Box>
            </Box>

              <Box sx={{ gridArea: 'title', display: 'flex', alignItems: 'flex-start', gap: { xs: 0.75, sm: 1.5 }, minWidth: 0, mt: { xs: 0, sm: 0 } }}>
                <Box
                  sx={{
                    width: headerExpanded ? { xs: 32, sm: 40 } : 28,
                    height: headerExpanded ? { xs: 32, sm: 40 } : 28,
                    borderRadius: 2,
                    bgcolor: 'rgba(255,255,255,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '1px solid rgba(255,255,255,0.25)',
                    flexShrink: 0,
                    '& svg': { color: 'white', fontSize: headerExpanded ? { xs: 18, sm: 22 } : 16 }
                  }}
                >
                  {tripTypeInfo.icon}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: DOCUMENT.display,
                      fontSize: headerExpanded
                        ? { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' }
                        : { xs: '1.35rem', sm: '1.5rem' },
                      fontWeight: 400,
                      lineHeight: 1.08,
                      color: DOCUMENT.ivory,
                      letterSpacing: '-0.01em',
                      wordBreak: 'break-word',
                      textShadow: headerCoverUrl ? '0 1px 10px rgba(36,19,24,0.35)' : 'none',
                    }}
                  >
                    {displayTripTitle}
                  </Typography>
                  {headerDateLine && (
                    <Typography
                      data-testid="trip-header-dates"
                      sx={{
                        mt: 0.4,
                        fontSize: { xs: 13, sm: 14 },
                        color: 'rgba(246,239,228,0.8)',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {headerDateLine}
                    </Typography>
                  )}
                  {headerExpanded && trip?.description && (
                    <Typography
                      variant="body2"
                      sx={{
                        mt: { xs: 0.5, sm: 1 },
                        mb: { xs: 0.25, sm: 0.5 },
                        fontSize: { xs: '0.8125rem', sm: '0.9375rem' },
                        fontWeight: 400,
                        lineHeight: 1.45,
                        color: 'rgba(246,239,228,0.85)',
                        maxWidth: { xs: '100%', sm: 480 },
                        wordBreak: 'break-word',
                      }}
                    >
                      {(() => {
                        const aiPattern = /^AI-generated trip with (\d+) days? of personalized recommendations$/i;
                        const match = trip?.description?.match(aiPattern);
                        if (match) {
                          const days = parseInt(match[1], 10);
                          return t('trip.aiGeneratedDesc', { days });
                        }
                        return trip?.description;
                      })()}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Collapsible section on mobile */}
          <Collapse in={headerExpanded} timeout={250}>
          <Box
            component="dl"
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: { xs: '12px 32px', sm: '0 40px' },
              mt: { xs: 1.5, sm: 2.5 },
              pt: 2,
              borderTop: '1px solid rgba(201,169,97,0.28)',
              mb: 1.5,
            }}
          >
            {trip && (
              <Box>
                <Typography component="dt" sx={{ fontSize: 10, letterSpacing: '0.19em', textTransform: 'uppercase', color: 'rgba(246,239,228,0.55)', mb: 0.5 }}>{t('trip.dates', { defaultValue: 'Dates' })}</Typography>
                <Typography component="dd" sx={{ fontSize: 15, color: DOCUMENT.ivory, m: 0, fontVariantNumeric: 'tabular-nums' }}>
                  {format(parseDateWithoutTimezone(trip.start_date), 'd MMM')} – {format(parseDateWithoutTimezone(trip.end_date), 'd MMM yyyy')}
                </Typography>
              </Box>
            )}
            {trip?.start_date && trip?.end_date && (
              <Box>
                <Typography component="dt" sx={{ fontSize: 10, letterSpacing: '0.19em', textTransform: 'uppercase', color: 'rgba(246,239,228,0.55)', mb: 0.5 }}>{t('itinerary.duration')}</Typography>
                <Typography component="dd" sx={{ fontSize: 15, color: DOCUMENT.ivory, m: 0, fontVariantNumeric: 'tabular-nums' }}>
                  {t('itinerary.day', { count: differenceInDays(parseDateWithoutTimezone(trip.end_date), parseDateWithoutTimezone(trip.start_date)) + 1 })}
                </Typography>
              </Box>
            )}
            <Box>
              <Typography component="dt" sx={{ fontSize: 10, letterSpacing: '0.19em', textTransform: 'uppercase', color: 'rgba(246,239,228,0.55)', mb: 0.5 }}>{t('trip.members')}</Typography>
              <Typography component="dd" sx={{ fontSize: 15, color: DOCUMENT.ivory, m: 0, fontVariantNumeric: 'tabular-nums' }}>
                {t('trip.memberCount', { count: visibleMemberCount })}
              </Typography>
            </Box>
            <Box>
              <Typography component="dt" sx={{ fontSize: 10, letterSpacing: '0.19em', textTransform: 'uppercase', color: 'rgba(246,239,228,0.55)', mb: 0.5 }}>{t('trip.statusLabel', { defaultValue: 'Status' })}</Typography>
              <Typography component="dd" sx={{ fontSize: 15, color: DOCUMENT.ivory, m: 0 }}>{tripStatus.label}</Typography>
            </Box>
          </Box>
          {/* Row 3: Meta info — location, creator, budget */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.25,
            rowGap: 1.25,
            flexWrap: 'wrap',
            overflow: 'hidden',
            fontSize: '0.85rem',
            mt: { xs: 0.25, sm: 0.5 },
            pt: { xs: 1.25, sm: 1.5 },
            pb: { xs: 0.5, sm: 0.75 },
            mb: { xs: 0.5, sm: 0 },
          }}>
            <Tooltip title={getTripAllLocations(trip)} arrow>
              <Chip
                component="div"
                role={tripMapsUrl ? 'link' : undefined}
                tabIndex={tripMapsUrl ? 0 : undefined}
                clickable={Boolean(tripMapsUrl)}
                icon={<LocationIcon />}
                label={isMobile ? getTripPrimaryLocation(trip) : getTripAllLocations(trip)}
                size="small"
                onClick={() => {
                  if (tripMapsUrl) openExternalURL(tripMapsUrl);
                }}
                onKeyDown={(event) => {
                  if (!tripMapsUrl) return;
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openExternalURL(tripMapsUrl);
                  }
                }}
                sx={{
                  display: 'inline-flex',
                  flexWrap: 'nowrap',
                  bgcolor: 'transparent',
                  color: DOCUMENT.ivory,
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  fontSize: { xs: '0.625rem', sm: '0.6875rem' },
                  height: 28,
                  maxWidth: { xs: '100%', sm: 'min(100%, 560px)' },
                  overflow: 'hidden',
                  cursor: tripMapsUrl ? 'pointer' : 'default',
                  borderRadius: 0,
                  border: '1px solid rgba(246,239,228,0.28)',
                  textDecoration: 'none',
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block',
                    letterSpacing: '0.12em',
                    py: 0,
                  },
                  '&:hover': { borderColor: DOCUMENT.accent, color: DOCUMENT.ivory },
                  '& .MuiChip-icon': { color: DOCUMENT.ivory, fontSize: 14 },
                }}
              />
            </Tooltip>
            <TripPassBadge trip={trip} variant="header" />
            {isGroupTrip(trip) && (
              <Tooltip title={t('trip.groupTripHint')} arrow>
                <Chip
                  icon={<GroupIcon />}
                  label={t('trip.groupTripBadge')}
                  size="small"
                  data-testid="trip-group-badge"
                  sx={{
                    bgcolor: 'transparent',
                    color: DOCUMENT.ivory,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    fontSize: { xs: '0.625rem', sm: '0.6875rem' },
                    height: 28,
                    borderRadius: 0,
                    border: `1px solid ${DOCUMENT.accent}`,
                    '& .MuiChip-label': { letterSpacing: '0.12em', py: 0 },
                    '& .MuiChip-icon': { color: DOCUMENT.accent, fontSize: 16 },
                  }}
                />
              </Tooltip>
            )}
            {getTripCreatorLabel(trip) && (
              <Chip
                avatar={(
                  <Avatar
                    src={trip.creator_avatar || undefined}
                    alt={getTripCreatorLabel(trip)}
                    sx={{ width: 18, height: 18, fontSize: 10, bgcolor: 'rgba(255,255,255,0.3)' }}
                  >
                    {getTripCreatorLabel(trip).charAt(0).toUpperCase()}
                  </Avatar>
                )}
                label={getTripCreatorLabel(trip)}
                size="small"
                sx={{
                  bgcolor: 'transparent',
                  color: DOCUMENT.ivory,
                  fontWeight: 500,
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  height: 28,
                  borderRadius: 0,
                  border: '1px solid rgba(246,239,228,0.28)',
                  maxWidth: { xs: '100%', sm: 280 },
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    py: 0,
                  },
                  '& .MuiChip-avatar': { width: 18, height: 18, ml: '6px' },
                }}
              />
            )}

            {/* Budget (compact) */}
            {trip?.budget && (
              <>
                <Chip
                  label={(() => {
                    const allCosts = [...itineraryItems, ...memberItineraryItems]
                      .filter(item => item.cost && parseFloat(item.cost) > 0)
                      .map(item => ({ amount: parseFloat(item.cost), currency: item.cost_currency || 'USD' }));
                    const convertedCosts = convertCostsForBudgetSummary(allCosts, userPreferences.preferredCurrency);
                    const totalSpent = convertedCosts.reduce((sum, cost) => sum + cost.convertedAmount, 0);
                    const budgetInfo = convertBudgetSummary(parseFloat(trip.budget), trip.budget_currency || 'USD', userPreferences.preferredCurrency);
                    return `${budgetInfo.symbol}${totalSpent.toFixed(0)} / ${budgetInfo.symbol}${budgetInfo.amount.toFixed(0)}`;
                  })()}
                  size="small"
                  sx={{
                    bgcolor: (() => {
                      const allCosts = [...itineraryItems, ...memberItineraryItems]
                        .filter(item => item.cost && parseFloat(item.cost) > 0)
                        .map(item => ({ amount: parseFloat(item.cost), currency: item.cost_currency || 'USD' }));
                      const convertedCosts = convertCostsForBudgetSummary(allCosts, userPreferences.preferredCurrency);
                      const totalSpent = convertedCosts.reduce((sum, cost) => sum + cost.convertedAmount, 0);
                      const budgetInfo = convertBudgetSummary(parseFloat(trip.budget), trip.budget_currency || 'USD', userPreferences.preferredCurrency);
                      const remaining = budgetInfo.amount - totalSpent;
                      const percentage = budgetInfo.amount > 0 ? (totalSpent / budgetInfo.amount) * 100 : 0;
                      if (remaining < 0) return '#ef4444';
                      if (percentage > 80) return '#f59e0b';
                      return 'rgba(16,185,129,0.4)';
                    })(),
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    height: { xs: 20, sm: 24 },
                    border: '1px solid rgba(255,255,255,0.2)',
                  }}
                />
              </>
            )}

          </Box>

          </Collapse>

          {canShowTripBookedBy(trip) && (
            <TripBookedBy
              name={String(trip.partner_company_name).trim()}
              logoUrl={trip.partner_logo}
              banner={partnerClientBrand ? tripBranding?.banner : undefined}
              onClick={() => setPartnerDetailsOpen(true)}
              action={!isPartner ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Button
                    data-testid="trip-request-change"
                    onClick={() => setRequestChangeOpen(true)}
                    sx={tripHeaderOutlineBtnSx}
                  >
                    {t('trip.requestChange')}
                  </Button>
                  <Button
                    data-testid="trip-message-manager"
                    disabled={messagingAgent}
                    onClick={openTripAgentMessages}
                    sx={tripHeaderOutlineBtnSx}
                  >
                    {messagingAgent ? t('myPackageBooking.openingMessages') : t('trip.messageManager')}
                  </Button>
                  {canChangeAdvisor && (
                    <Button
                      data-testid="trip-switch-advisor-open"
                      startIcon={<SupportAgentIcon />}
                      onClick={() => setHandoverDialogOpen(true)}
                      sx={tripHeaderOutlineBtnSx}
                    >
                      {t('trip.switchAdvisorCta')}
                    </Button>
                  )}
                </Box>
              ) : null}
            />
          )}
          {isManagedByPartner(trip) && !canShowTripBookedBy(trip) && !isPartner && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1.75 }}>
              <Button
                data-testid="trip-request-change"
                onClick={() => setRequestChangeOpen(true)}
                sx={tripHeaderOutlineBtnSx}
              >
                {t('trip.requestChange')}
              </Button>
              <Button
                data-testid="trip-message-manager"
                disabled={messagingAgent}
                onClick={openTripAgentMessages}
                sx={tripHeaderOutlineBtnSx}
              >
                {messagingAgent ? t('myPackageBooking.openingMessages') : t('trip.messageManager')}
              </Button>
              {canChangeAdvisor && (
                <Button
                  data-testid="trip-switch-advisor-open"
                  startIcon={<SupportAgentIcon />}
                  onClick={() => setHandoverDialogOpen(true)}
                  sx={tripHeaderOutlineBtnSx}
                >
                  {t('trip.switchAdvisorCta')}
                </Button>
              )}
            </Box>
          )}
          {canChangeAdvisor && !isManagedByPartner(trip) && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1.75 }}>
              <Button
                data-testid="trip-handover-open"
                startIcon={<SupportAgentIcon />}
                onClick={() => setHandoverDialogOpen(true)}
                sx={tripHeaderOutlineBtnSx}
              >
                {t('trip.handoverCta')}
              </Button>
            </Box>
          )}

          <Box 
            onClick={() => setHeaderExpanded(!headerExpanded)}
            data-testid="trip-header-expand"
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              mt: 0.25,
              cursor: 'pointer',
              py: 0.5,
              borderRadius: 2,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
            }}
          >
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem', mr: 0.5 }}>
              {headerExpanded ? t('common.showLess') : t('trip.showDetails')}
            </Typography>
            {headerExpanded ? 
              <ExpandLessIcon sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 18 }} /> : 
              <ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 18 }} />
            }
          </Box>
          </Box>

          <Menu
            anchorEl={headerMenuAnchor}
            open={Boolean(headerMenuAnchor)}
            onClose={() => setHeaderMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                mt: 0.75,
                minWidth: 220,
              },
            }}
          >
            <MenuItem
              onClick={() => {
                pdfExportRef.current?.handleClick();
                setHeaderMenuAnchor(null);
              }}
              disabled={loading}
            >
              <ListItemIcon>
                <PdfIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t('trip.exportPdf')} />
            </MenuItem>
            <MenuItem
              onClick={() => {
                calendarExportRef.current?.querySelector('button')?.click();
                setHeaderMenuAnchor(null);
              }}
              disabled={loading}
            >
              <ListItemIcon>
                <CalendarIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t('trip.exportCalendar')} />
            </MenuItem>
            {canChangeAdvisor && (
              <>
                <Divider sx={{ display: { xs: 'none', md: 'block' } }} />
                <MenuItem
                  onClick={() => {
                    setHandoverDialogOpen(true);
                    setHeaderMenuAnchor(null);
                  }}
                  data-testid={isManagedByPartner(trip) ? 'trip-switch-advisor-menu' : 'trip-handover-menu'}
                >
                  <ListItemIcon>
                    <SupportAgentIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t(isManagedByPartner(trip) ? 'trip.switchAdvisorCta' : 'trip.handoverCta')}
                    secondary={t(isManagedByPartner(trip) ? 'trip.switchAdvisorMenuHint' : 'trip.handoverMenuHint')}
                  />
                </MenuItem>
              </>
            )}
            {isCreator && (
              <>
                <Divider sx={{ display: { xs: 'none', md: 'block' } }} />
                <MenuItem
                  onClick={() => {
                    setPublishGuideOpen(true);
                    setHeaderMenuAnchor(null);
                  }}
                  sx={{ display: { xs: 'none', md: 'flex' } }}
                >
                  <ListItemIcon>
                    <MenuBookIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('publishGuide.title')}
                    secondary={t('publishGuide.menuHint')}
                  />
                </MenuItem>
              </>
            )}
            {canArchivePastTrip && (
              <>
                <Divider />
                <MenuItem
                  data-testid="trip-details-archive-menu"
                  onClick={() => {
                    setArchiveError('');
                    setArchiveDialogOpen(true);
                    setHeaderMenuAnchor(null);
                  }}
                >
                  <ListItemIcon>
                    <ArchiveIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={t('trip.archiveTrip')} />
                </MenuItem>
              </>
            )}
          </Menu>

          <Box
            sx={{
              position: 'fixed',
              width: 1,
              height: 1,
              padding: 0,
              margin: -1,
              overflow: 'hidden',
              clip: 'rect(0, 0, 0, 0)',
              whiteSpace: 'nowrap',
              border: 0,
              top: 0,
              left: 0,
              pointerEvents: 'none',
            }}
            aria-hidden="true"
          >
            <PDFExportButton
              ref={pdfExportRef}
              tripId={id}
              tripTitle={trip?.title || 'Trip'}
              disabled={loading}
              usePartnerAuth={isPartner}
            />
            <Box ref={calendarExportRef}>
              <CalendarExportButton
                tripId={id}
                tripTitle={trip?.title || 'Trip'}
                disabled={loading}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Page tabs — full-bleed oxblood bar, brass underline, text only */}
      <Box sx={{ bgcolor: headerBgFromClientBranding(tripBranding) || DOCUMENT.nav, borderBottom: `1px solid ${DOCUMENT.navRule}` }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, md: 5 }, py: 0 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            textColor="inherit"
            TabIndicatorProps={{ sx: documentTabIndicatorSx }}
            sx={documentPageTabsSx}
          >
            {visibleTabs.map((tab) => (
              <Tab
                key={tab.key}
                value={tab.key}
                label={
                  tab.key === 'chat' ? (
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                      {t(tab.labelKey)}
                      {tabValue !== 'chat' && stickyUnreadCount > 0 && (
                        <Box component="span" sx={documentChatCountSx}>
                          {stickyUnreadCount}
                        </Box>
                      )}
                    </Box>
                  ) : (
                    t(tab.labelKey)
                  )
                }
              />
            ))}
          </Tabs>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 }, position: 'relative', zIndex: 2 }}>
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: THEME_RADIUS.small,
              ...COMMON_STYLES.professionalCard
            }}
          >
            {error}
          </Alert>
        )}

        {trip?.admin_view && (
          <Alert
            severity="info"
            data-testid="trip-admin-support-banner"
            sx={{ mb: 3, borderRadius: THEME_RADIUS.small }}
          >
            {t('trip.adminSupportView')}
          </Alert>
        )}

        {/* Tab Content */}
        <Grid container spacing={3} data-testid="trip-details-panels">
          <Grid item xs={12}>

          <TabPanel value={tabValue} index="itinerary">
            <OfflineItineraryBar
              supported={offlineTrip.supported}
              isOnline={isOnline && !viewingSnapshot}
              isSaved={offlineTrip.isSaved}
              savedAt={offlineTrip.savedAt}
              saving={offlineTrip.saving}
              error={offlineTrip.error}
              hasTripPass
              onSave={offlineTrip.save}
              onRemove={offlineTrip.remove}
            />
            {(isCreator || isPartner) && !viewingSnapshot && (
              <EmailForwardingCard
                compact
                variant={isPartner ? 'partner' : 'traveler'}
                context="tripDetail"
              />
            )}
            <TripItinerary 
              ref={tripItineraryRef}
              tripId={id} 
              isCreator={isCreator}
              isPartner={isPartner} 
              trip={trip}
              onItineraryChange={handleItineraryChange}
              onItemExpand={(itemId) => {
                if (trip?.partner_id || trip?.managed_by_partner) {
                  recordTripEngagement(api, id, {
                    eventType: 'item_view',
                    itineraryItemId: itemId,
                    email: user?.email,
                  });
                }
              }}
              initialTab={itineraryInitialTab}
              onNavigateToSuggestions={() => {
                setItineraryInitialTab('suggestions');
                setTabValue('itinerary');
              }}
              onNavigateToExplore={() => {
                // Explore is now a trip-level tab
                setTabValue('explore');
              }}
              onNavigateToAIConcierge={canShowTripSuggestWithAi(trip, { isCreator, isPartner })
                ? () => setSuggestAiOpen(true)
                : canShowTripAiGenerate(trip)
                  ? () => navigate('/ai-concierge')
                  : undefined}
              onSuggestWithAi={canShowTripSuggestWithAi(trip, { isCreator, isPartner })
                ? () => setSuggestAiOpen(true)
                : undefined}
            />
          </TabPanel>

          {!isPartner && isManagedByPartner(trip) && (
            <TabPanel value={tabValue} index="billing">
              <TripGroupInvoice tripId={id} />
            </TabPanel>
          )}

          <TabPanel value={tabValue} index="memories">
            <TripMemoriesPanel trip={trip} isCreator={isCreator} />
          </TabPanel>

          {/* Checklist Tab */}
          <TabPanel value={tabValue} index="checklist">
            <TripChecklist 
              tripId={id}
            />
          </TabPanel>

          {!isPartner && (
            <TabPanel value={tabValue} index="explore">
              <TripMapExplorerNew 
                key={`map-explorer-${id}`}
                trip={trip}
                isCreator={isCreator}
                isPartner={isPartner}
                isActive={tabValue === 'explore'}
                onAddActivity={(activityData) => {
                  setTabValue('itinerary');
                  setTimeout(() => {
                    tripItineraryRef.current?.openAddActivityDialog(activityData);
                  }, 100);
                }}
                onCreateSuggestion={(activityData) => {
                  setTabValue('itinerary');
                  setTimeout(() => {
                    tripItineraryRef.current?.openSuggestionFromExplore(activityData);
                  }, 100);
                }}
              />
            </TabPanel>
          )}

          <TabPanel value={tabValue} index="members">
            <TripMembers 
              tripId={id} 
              isCreator={isCreator}
              isPartner={isPartner} 
              onInviteClick={() => setShowInviteDialog(true)}
              onMemberChange={fetchTripDetails}
              ref={tripMembersRef}
              memberCount={visibleMemberCount}
            />
          </TabPanel>

          {!isPartner && (
            <TabPanel value={tabValue} index="map">
              <TripMemberMap 
                tripId={id} 
                trip={trip}
              />
            </TabPanel>
          )}

          {!isPartner && (
            <TabPanel value={tabValue} index="expenses">
              <TripExpenses 
                tripId={id} 
                trip={trip}
              />
            </TabPanel>
          )}

          {!isPartner && (
            <TabPanel value={tabValue} index="flights">
              <TripFlightAlerts
                tripId={id}
                trip={trip}
                isActive={tabValue === 'flights'}
              />
            </TabPanel>
          )}

          {!isPartner && (
            <TabPanel value={tabValue} index="documents">
              <TripDocuments
                tripId={id}
                currentUserId={user?.id}
                isCreator={isCreator}
                isActive={tabValue === 'documents'}
                focusDocumentId={new URLSearchParams(location.search).get('doc')}
                onFocusDocumentHandled={() => {
                  const params = new URLSearchParams(location.search);
                  if (!params.has('doc')) return;
                  params.delete('doc');
                  const next = params.toString();
                  navigate(
                    `${location.pathname}${next ? `?${next}` : ''}${location.hash || ''}`,
                    { replace: true }
                  );
                }}
              />
            </TabPanel>
          )}

          {canViewHistory && (
            <TabPanel value={tabValue} index="history">
              <ChangeHistoryPanel
                endpoint={`/trips/${id}/history`}
                subtitle={t('changeHistory.tripSubtitle')}
                testId="trip-history"
              />
            </TabPanel>
          )}

          {/* Chat Tab - render directly without extra padding */}
          {!isPartner && (
            <div
              role="tabpanel"
              hidden={tabValue !== 'chat'}
              id="simple-tabpanel-chat"
              aria-labelledby="simple-tab-chat"
            >
              {tabValue === 'chat' && (
              <Fade in timeout={300}>
                <Box sx={{ pt: 0.5 }}>
                  <TripGroupChat 
                    tripId={id} 
                    currentUser={user}
                    isTabActive={tabValue === 'chat'}
                    onUnreadCountChange={handleUnreadCountChange}
                    tripTitle={trip?.title || ''}
                  />
                </Box>
                </Fade>
              )}
            </div>
          )}

          {/* Background message listener for unread count when chat tab is not active */}
          {!isPartner && tabValue !== 'chat' && (
            <BackgroundChatListener 
              tripId={id} 
              currentUser={user}
              onUnreadCountChange={handleUnreadCountChange}
              tripTitle={trip?.title || ''}
            />
          )}

          </Grid>
        </Grid>

      </Container>

      <Dialog
        open={archiveDialogOpen}
        onClose={() => !archiveLoading && setArchiveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('trip.archiveDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('trip.archiveDialog.message', { title: trip?.title })}
          </DialogContentText>
          {archiveError ? <Alert severity="error" sx={{ mt: 2 }}>{archiveError}</Alert> : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveDialogOpen(false)} disabled={archiveLoading}>
            {t('trip.archiveDialog.cancel')}
          </Button>
          <Button
            onClick={handleArchiveTrip}
            variant="contained"
            disabled={archiveLoading}
            data-testid="trip-details-archive-confirm"
          >
            {archiveLoading ? t('trip.archiveDialog.archiving') : t('trip.archiveTrip')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: THEME_RADIUS.small,
            boxShadow: THEME_SHADOWS.heavy,
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            background: THEME_GRADIENTS.error,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <DeleteIcon />
          Delete Trip
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <DialogContentText sx={{ fontSize: '1rem', lineHeight: 1.6 }}>
            Are you sure you want to delete "<strong>{trip?.title}</strong>"? 
            This action cannot be undone and will remove all trip data, including itinerary items, 
            suggestions, and chat messages.
          </DialogContentText>
        </DialogContent>
        
        <DialogActions sx={{ 
          p: { xs: 2, sm: 3 }, 
          pt: 0,
          gap: { xs: 1, sm: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          '& .MuiButton-root': {
            minHeight: { xs: 48, sm: 44 },
            width: { xs: '100%', sm: 'auto' },
            fontSize: { xs: '1rem', sm: '0.875rem' }
          }
        }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ 
              px: { xs: 4, sm: 3 },
              textTransform: 'none',
              fontWeight: 600,
              order: { xs: 2, sm: 1 }
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleDelete}
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
            sx={{
              px: { xs: 4, sm: 3 },
              py: { xs: 1.5, sm: 1.5 },
              fontWeight: 700,
              borderRadius: THEME_RADIUS.small,
              order: { xs: 1, sm: 2 }
            }}
          >
            {deleteLoading ? 'Deleting...' : 'Delete Trip'}
          </Button>
        </DialogActions>
      </Dialog>

      <HandOverTripToAgentDialog
        open={handoverDialogOpen}
        onClose={() => setHandoverDialogOpen(false)}
        tripId={id}
        mode={isManagedByPartner(trip) ? 'switch' : 'assign'}
        excludePartnerId={trip?.partner_id || null}
        onSuccess={(data) => {
          const switched = Boolean(data?.switched);
          setSnackbarMessage(
            data?.partnerName
              ? t(switched ? 'trip.switchAdvisorSuccessNamed' : 'trip.handoverSuccessNamed', {
                name: data.partnerName,
              })
              : t(switched ? 'trip.switchAdvisorSuccess' : 'trip.handoverSuccess')
          );
          setSnackbarOpen(true);
          fetchTripDetails();
        }}
      />

      {/* Invite Member Dialog */}
      <Dialog 
        open={showInviteDialog} 
        onClose={() => setShowInviteDialog(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: THEME_RADIUS.small,
            boxShadow: THEME_SHADOWS.heavy,
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            backgroundColor: THEME_COLORS.primary.main,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <PersonAddIcon />
          Invite Member to Trip
        </DialogTitle>
        
        {/* Wrap content in a form to use handleInvite */}
        <form onSubmit={handleInvite}>
          <DialogContent sx={{ p: 3 }}>
            {inviteError && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  borderRadius: THEME_RADIUS.small,
                }}
              >
                {inviteError}
              </Alert>
            )}
            
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="Enter member's email address"
              autoFocus
              sx={{
                mt: 1,
                ...COMMON_STYLES.professionalInput,
              }}
            />
          </DialogContent>
          <DialogActions sx={{ 
            p: { xs: 2, sm: 3 }, 
            pt: 0,
            gap: { xs: 1, sm: 2 },
            flexDirection: { xs: 'column', sm: 'row' },
            '& .MuiButton-root': {
              minHeight: { xs: 48, sm: 44 },
              width: { xs: '100%', sm: 'auto' },
              fontSize: { xs: '1rem', sm: '0.875rem' }
            }
          }}>
            <Button 
              onClick={() => setShowInviteDialog(false)}
              sx={{ 
                px: { xs: 4, sm: 3 },
                textTransform: 'none',
                fontWeight: 600,
                order: { xs: 2, sm: 1 }
              }}
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              variant="contained"
              disabled={!inviteEmail.trim()}
              startIcon={<PersonAddIcon />}
              sx={{
                px: { xs: 4, sm: 3 },
                borderRadius: THEME_RADIUS.small,
                textTransform: 'none',
                fontWeight: 700,
                backgroundColor: THEME_COLORS.primary.main,
                color: 'white',
                order: { xs: 1, sm: 2 },
                '&:hover': {
                  backgroundColor: THEME_COLORS.primary.dark,
                  transform: 'translateY(-1px)',
                  boxShadow: THEME_SHADOWS.medium
                },
                '&:disabled': {
                  backgroundColor: THEME_COLORS.text.disabled,
                  color: 'white',
                  opacity: 0.6
                },
              }}
            >
              Send Invitation
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <PartnerTripAIDrawer
        open={suggestAiOpen}
        onClose={() => setSuggestAiOpen(false)}
        trip={trip}
        usePartnerAuth={isPartner}
        onAdded={async (created) => {
          handleItineraryChange();
          await tripItineraryRef.current?.refreshItinerary?.();
          tripItineraryRef.current?.focusDrafts?.(created);
        }}
      />

      {/* Snackbar for copy confirmation */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />

      {/* Share Dialog */}
      <Dialog 
        open={shareDialogOpen} 
        onClose={() => setShareDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: THEME_RADIUS.small,
            boxShadow: THEME_SHADOWS.heavy,
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            backgroundColor: THEME_COLORS.primary.main,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <ShareIcon />
          Share Trip
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
            {trip?.title}
          </Typography>
          
          <Typography variant="body2" sx={{ mb: 3, color: theme.palette.text.secondary }}>
            Share this trip with your friends and family on social media or copy the link to share anywhere.
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 2 }}>
            {/* Twitter */}
            <Button
              variant="outlined"
              startIcon={<TwitterIcon />}
              onClick={() => {
                const { shareText } = getShareData();
                const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
                window.open(url, '_blank');
                setShareDialogOpen(false);
              }}
              sx={{
                color: '#1DA1F2',
                borderColor: '#1DA1F2',
                '&:hover': {
                  backgroundColor: '#1DA1F2',
                  color: 'white',
                  borderColor: '#1DA1F2',
                }
              }}
            >
              Twitter
            </Button>
            
            {/* Facebook */}
            <Button
              variant="outlined"
              startIcon={<FacebookIcon />}
              onClick={() => {
                const { shareText } = getShareData();
                const url = `https://www.facebook.com/sharer/sharer.php?quote=${encodeURIComponent(shareText)}`;
                window.open(url, '_blank');
                setShareDialogOpen(false);
              }}
              sx={{
                color: '#4267B2',
                borderColor: '#4267B2',
                '&:hover': {
                  backgroundColor: '#4267B2',
                  color: 'white',
                  borderColor: '#4267B2',
                }
              }}
            >
              Facebook
            </Button>
            
            {/* LinkedIn */}
            <Button
              variant="outlined"
              startIcon={<LinkedInIcon />}
              onClick={() => {
                const { shareText } = getShareData();
                const title = encodeURIComponent(`${trip?.title} - ${shareBrandName}`);
                const url = `https://www.linkedin.com/sharing/share-offsite/?title=${title}&summary=${encodeURIComponent(shareText)}`;
                window.open(url, '_blank');
                setShareDialogOpen(false);
              }}
              sx={{
                color: '#0077B5',
                borderColor: '#0077B5',
                '&:hover': {
                  backgroundColor: '#0077B5',
                  color: 'white',
                  borderColor: '#0077B5',
                }
              }}
            >
              LinkedIn
            </Button>
            
            {/* WhatsApp */}
            <Button
              variant="outlined"
              startIcon={<WhatsAppIcon />}
              onClick={() => {
                const { shareText } = getShareData();
                const url = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
                window.open(url, '_blank');
                setShareDialogOpen(false);
              }}
              sx={{
                color: '#25D366',
                borderColor: '#25D366',
                '&:hover': {
                  backgroundColor: '#25D366',
                  color: 'white',
                  borderColor: '#25D366',
                }
              }}
            >
              WhatsApp
            </Button>
            
            {/* Copy Text */}
            <Button
              variant="contained"
              startIcon={<CopyIcon />}
              onClick={handleCopyLink}
              sx={{
                backgroundColor: THEME_COLORS.text.secondary,
                color: 'white',
                gridColumn: 'span 2',
                '&:hover': {
                  backgroundColor: THEME_COLORS.text.primary,
                }
              }}
            >
              Copy Text
            </Button>

            {/* Email Share Button */}
            <Button
              variant="contained"
              startIcon={<EmailIcon />}
              onClick={() => setEmailShareDialogOpen(true)}
              sx={{
                backgroundColor: THEME_COLORS.primary.main,
                color: 'white',
                gridColumn: 'span 2',
                '&:hover': {
                  backgroundColor: THEME_COLORS.primary.dark,
                }
              }}
            >
              Share via Email
            </Button>

            {(isCreator || isPartner) && (
              <Button
                variant="outlined"
                startIcon={<LanguageIcon />}
                onClick={() => {
                  setShareDialogOpen(false);
                  setReadOnlyShareOpen(true);
                }}
                sx={{ gridColumn: 'span 2', textTransform: 'none' }}
              >
                {t('tripShare.openFromShare')}
              </Button>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={() => setShareDialogOpen(false)}
            sx={{ 
              px: 3,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <ShareReadOnlyLinkDialog
        open={readOnlyShareOpen}
        onClose={() => setReadOnlyShareOpen(false)}
        tripId={id}
        usePartnerAuth={isPartner}
      />

      {/* Email Share Dialog */}
      <Dialog open={emailShareDialogOpen} onClose={() => setEmailShareDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Share Trip Itinerary</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Share your trip itinerary with friends and family. They'll receive a detailed daily schedule of all activities.
          </Typography>
          <TextField
            label="Recipient Email"
            type="email"
            fullWidth
            value={emailShareRecipient}
            onChange={e => setEmailShareRecipient(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Personal Message (optional)"
            multiline
            minRows={2}
            fullWidth
            value={emailShareMessage}
            onChange={e => setEmailShareMessage(e.target.value)}
            placeholder="Add a personal note to your itinerary email..."
            sx={{ mb: 2 }}
          />
          {emailShareError && <Alert severity="error">{emailShareError}</Alert>}
          {emailShareSuccess && <Alert severity="success">{emailShareSuccess}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailShareDialogOpen(false)} disabled={emailShareLoading}>Cancel</Button>
          <Button onClick={handleSendItineraryEmail} variant="contained" disabled={emailShareLoading || !emailShareRecipient}>
            {emailShareLoading ? 'Sending...' : 'Send Itinerary'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Wall */}
      <PaymentWall
        open={paymentWallOpen}
        onClose={handlePaymentWallClose}
        tripId={id}
        tripTitle={trip?.title || 'Trip'}
        isOwner={isCreator}
        feature={blockedFeature}
        onSuccess={() => {
          refreshAccess();
          fetchTripDetails();
        }}
      />

      {/* Leave Trip Confirmation Dialog */}
      <Dialog open={leaveDialogOpen} onClose={() => setLeaveDialogOpen(false)}>
        <DialogTitle>Leave Trip</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to leave this trip? You will no longer have access to the trip details, itinerary, and group chat.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveDialogOpen(false)} disabled={leaveLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleLeaveTrip} 
            variant="contained" 
            color="error"
            disabled={leaveLoading}
          >
            {leaveLoading ? 'Leaving...' : 'Leave Trip'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Development Paywall Bypass Toggle */}
      <PaywallBypassToggle />

      {/* Edit Trip Modal */}
      <TripModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        mode="edit"
        tripId={id}
        onSuccess={() => {
          // Refresh trip data after successful edit
          fetchTripDetails();
        }}
      />

      <CreateOpsRequestDialog
        open={requestChangeOpen}
        mode="traveler"
        tripId={trip?.id || id}
        tripLabel={trip ? getDisplayTripTitle(trip) : ''}
        onClose={() => setRequestChangeOpen(false)}
      />

      {/* Partner Details Dialog */}
      <Dialog 
        open={partnerDetailsOpen} 
        onClose={() => setPartnerDetailsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {t('trip.managedBy', { name: trip?.partner_company_name || t('trip.partner') })}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {trip && (
            <Box sx={{ py: 2 }}>
              <Paper elevation={0} sx={{ bgcolor: '#f8f9fa', p: 3, borderRadius: 2, mb: 3 }}>
                {isUsableImageUrl(trip.partner_logo) ? (
                  <PartnerLockup
                    src={trip.partner_logo}
                    alt={trip.partner_company_name}
                    banner={partnerClientBrand ? tripBranding?.banner : undefined}
                    height={48}
                    maxWidth={180}
                    sx={{ mb: 1.5 }}
                  />
                ) : (
                  <PartnerLogoMark
                    partner={{ company_name: trip.partner_company_name }}
                    alt={trip.partner_company_name || ''}
                    sx={{
                      width: 48,
                      height: 48,
                      mb: 1.5,
                      bgcolor: DOCUMENT.canvas,
                      border: `1px solid ${DOCUMENT.rule}`,
                      color: DOCUMENT.oxblood,
                      fontSize: 16,
                      fontWeight: 600,
                    }}
                  />
                )}
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: brandColor }}>
                  {trip.partner_company_name}
                </Typography>
                {partnerClientBrand && tripBranding?.showHouseMarks && (
                  <Typography variant="caption" color="text.secondary">
                    {t('trip.poweredBy')}
                  </Typography>
                )}
                
                <Stack spacing={2} sx={{ mt: 2 }}>
                  {trip.partner_contact_name && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <PersonIcon color="action" />
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Contact Person
                        </Typography>
                        <Typography variant="body1">
                          {trip.partner_contact_name}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  
                  {trip.partner_website && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <LanguageIcon color="action" />
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Website
                        </Typography>
                        <Typography variant="body1">
                          <a 
                            href={trip.partner_website.startsWith('http') ? trip.partner_website : `https://${trip.partner_website}`} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            style={{ color: '#667eea', textDecoration: 'none' }}
                          >
                            {trip.partner_website}
                          </a>
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Stack>
              </Paper>

              <Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>
                <Typography variant="body2">
                  {t('myInquiries.contactThroughInquiry')}
                </Typography>
              </Alert>

              <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                <Typography variant="body2">
                  This partner is managing the itinerary for this trip on your behalf. 
                  You maintain full ownership and control of the trip, including adding members, 
                  managing expenses, and all other trip features.
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPartnerDetailsOpen(false)} variant="contained" sx={{ bgcolor: '#667eea' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish as Travel Guide Dialog */}
      {isCreator && (
        <PublishGuideDialog
          open={publishGuideOpen}
          onClose={() => setPublishGuideOpen(false)}
          trip={trip}
          tripId={id}
        />
      )}
    </Box>
  );
};

export default TripDetails; 